// 거래소 ↔ 장비 장부 연결(docs/PARTY-TRADE-PRD.md 1.1, 레인 D 2단계): 거래소 창(MarketClient)이 쓰는 MarketLedger를 GameSim 장부로 만든다.
// - bagItems: 가방(장착 제외) 장비 / gold: 지금 쓸 수 있는 골드(profile 계산값)
// - out: mkOut(맡긴 장비를 가방에서 뺀다) / goldOut: mkGold(−amount, 골드 맡기기)
// 연산 id는 거래 id에서 결정적(mk:<idem>, mk:rec:<uid>)이라 같은 일을 두 번 해도 한 번만 들어간다(duplicate도 성공으로 본다).
import type { MarketLedger } from '../market/marketClient';
import type { GearItem } from '../shared/gear';
import type { GearCode, GearOp } from '../shared/gearLedger';

export interface MarketGame {
  readonly gear: {
    readonly items: readonly GearItem[];
    readonly equipped: Readonly<Record<string, string | null>>;
    /** 영웅별 무기 칸 전체(없는 옛 보기는 equipped만) */
    readonly heroWeapons?: Readonly<Partial<Record<string, string>>>;
    applyMarket(op: Extract<GearOp, { t: 'mkIn' | 'mkGold' | 'mkOut' }>): GearCode;
    mayHold(uid: string): boolean;
  };
  readonly profile: { readonly gold: number; readonly mode: string; sync(): void };
}

/** 마지막 장부 결과(UI 문구용) */
export let lastMarketCode: GearCode = 'ok';

export function marketLedger(game: MarketGame): MarketLedger {
  const done = (code: GearCode): boolean => { lastMarketCode = code; return code === 'ok' || code === 'duplicate'; };
  return {
    bagItems() {
      // 지금 영웅 칸 + 다른 영웅 무기 칸(heroWeapons)에 낀 것은 모두 거래 대상에서 뺀다
      const on = new Set([...Object.values(game.gear.equipped), ...Object.values(game.gear.heroWeapons ?? {})].filter((u): u is string => !!u));
      return game.gear.items.filter((it) => !on.has(it.uid));
    },
    gold() { game.profile.sync(); return game.profile.gold; },
    async out(uid, opId) {
      return done(game.gear.applyMarket({ id: opId, t: 'mkOut', uid }));
    },
    hasItem(uid) { return game.gear.mayHold(uid); },
    async goldOut(amount, opId) {
      // 골드가 움직이는 연산은 이 탭이 상점 장부 writer일 때만(applyMail과 같은 규칙)
      if (game.profile.mode !== 'ok') return done('readonly');
      game.profile.sync();
      const ok = done(game.gear.applyMarket({ id: opId, t: 'mkGold', d: -amount, gold: game.profile.gold }));
      game.profile.sync();
      return ok;
    },
  };
}
